import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { readFileAsDataUrl } from 'utils/fileUtils';
import parseDataUrl from 'parse-data-url';
import {
  Button,
  Form,
  FormField,
  Image,
  Label,
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from 'semantic-ui-react';
import { Field, Form as FinalForm } from 'react-final-form';
import ValidatedFormInput from 'components/ValidatedFormInput';
import {
  numericOnlyNormalization,
  requiredStringValidation,
  simpleSetValueFormMutator,
} from 'utils/formUtils';
import ImageUploader from 'components/ImageUploader';

const validateImageUploaded = initialImage => value =>
  !value && !initialImage ? 'Image is required' : undefined;

function EditNpwpModal({ shown, onClose, onSubmit, isLoading }) {
  let submit;
  let formMutators;
  let formInitialize;

  const initialValues = useSelector(state => state.npwp.businessDetail.npwp);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    if (shown) {
      const obj = initialValues || {};
      formInitialize({
        npwp_no: obj.number,
        npwp_name: obj.name,
        npwp_image: null,
      });
      setImagePreview(null);
    }
  }, [formInitialize, initialValues, shown]);

  const onFileDrop = async files => {
    const url = await readFileAsDataUrl(files[0]);
    const parsed = parseDataUrl(url);
    setImagePreview(url);
    formMutators.setValue('npwp_image', parsed.data);
  };

  const renderForm = ({ handleSubmit, form: { mutators, initialize } }) => {
    submit = handleSubmit;
    formMutators = mutators;
    formInitialize = initialize;
    return (
      <Form>
        <FormField>
          <label htmlFor="npwp-number">NPWP Number:</label>
          <Field
            id="npwp-number"
            name="npwp_no"
            component={ValidatedFormInput}
            parse={numericOnlyNormalization}
            validate={requiredStringValidation()}
          />
        </FormField>
        <FormField>
          <label htmlFor="npwp-name">NPWP Name:</label>
          <Field
            id="npwp-name"
            name="npwp_name"
            component={ValidatedFormInput}
            validate={requiredStringValidation()}
          />
        </FormField>
        <FormField>
          <label>NPWP Image:</label>
          {(!!initialValues.image || !!imagePreview) && (
            <Image src={imagePreview || initialValues.image} size="medium" />
          )}
          <ImageUploader onChange={onFileDrop} />
          <Field
            name="npwp_image"
            validate={validateImageUploaded(initialValues.image)}
          >
            {({ meta: { touched, error } }) => {
              const errorContent = touched && !!error && error;
              return errorContent && <Label prompt content={errorContent} />;
            }}
          </Field>
        </FormField>
      </Form>
    );
  };

  renderForm.propTypes = {
    handleSubmit: PropTypes.func.isRequired,
    form: PropTypes.shape({
      mutators: PropTypes.object.isRequired,
      initialize: PropTypes.func.isRequired,
    }).isRequired,
  };

  return (
    <Modal open={shown}>
      <ModalHeader>Edit NPWP</ModalHeader>
      <ModalContent>
        <FinalForm
          onSubmit={onSubmit}
          mutators={simpleSetValueFormMutator}
          render={renderForm}
        />
      </ModalContent>
      <ModalActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          positive
          onClick={event => submit(event)}
          disabled={isLoading}
          loading={isLoading}
        >
          Submit
        </Button>
      </ModalActions>
    </Modal>
  );
}

EditNpwpModal.propTypes = {
  shown: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

export default EditNpwpModal;
